import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: TICKET.
 *
 * Vendor-agnostic projection of work-tracking tickets / issues across the
 * three connected providers Screenata reads from: GitHub Issues, Jira,
 * Linear. Captures the cross-provider compliance 80%: identifier, URL,
 * title, lifecycle state, labels, assignee, timestamps.
 *
 * Used by the discovery-style native checks (e.g. `github.search-issues`):
 * the check pulls matched tickets via the provider adapter, ingests each
 * one through the substrate channel as `TICKET`, and the agent reads them
 * back via `populationListAsOfRun` to surface "is there already a closed
 * ticket covering this finding?" without a separate ticketing-tool call.
 *
 * Provider-specific knobs (Jira priority levels, Linear cycle, GitHub
 * draft-PR semantics) go on `providerExtensions` when a check needs them.
 *
 * NOTE: separate from `RemediationTicket` (the durable work-tracking row
 * Screenata files itself). TICKET is for *observation* of pre-existing
 * external tickets; RemediationTicket is for *ownership* of compliance
 * work.
 */

export const TICKET_SCHEMA_VERSION = 1;

export const Ticket = z.object({
  /** Stable provider identifier. GitHub = `github:<repoId>:<issueNumber>`,
   *  Jira = `jira:<issueKey>`, Linear = `linear:<identifier>`. */
  externalId: z.string(),

  /** Human-readable identifier the user sees: GitHub `#42`, Jira `SEC-123`,
   *  Linear `SEC-123`. Stable across rename of the parent repo/project. */
  identifier: z.string(),

  /** Web URL where a reviewer can open the ticket. */
  url: z.string(),

  /** Short title. Not guaranteed unique. */
  title: z.string(),

  /** Cross-provider lifecycle state. Providers diverge on subtler states
   *  (Jira "In Progress", Linear "In Review"); the canonical strips those
   *  to the three states that matter for evidence discovery. */
  state: z.enum(['open', 'closed', 'in_progress']),

  /** Body / description in markdown when available. Null when the
   *  provider didn't return it (search APIs often omit body for perf). */
  body: z.string().nullable(),

  /** Parent grouping identifier — GitHub `owner/repo`, Jira project key,
   *  Linear team key. Stable. */
  parentId: z.string().nullable(),

  /** Labels / tags on the ticket. Empty array when none. */
  labels: z.array(z.string()),

  /** Assignee login / email / account-id when available. Single value
   *  even on multi-assignee platforms — projections pick the first. */
  assignee: z.string().nullable(),

  /** When the ticket was created. */
  createdAt: z.string().datetime().nullable(),

  /** When the ticket last moved (status change, comment, edit). */
  updatedAt: z.string().datetime().nullable(),

  /** When the ticket was closed/resolved/done. Null while open. */
  closedAt: z.string().datetime().nullable(),

  /** Escape hatch for provider-specific fields (Jira priority, Linear
   *  cycle, GitHub assignees[] for multi-assignee tickets). */
  providerExtensions: z.record(z.unknown()).optional(),
});

export type Ticket = z.infer<typeof Ticket>;
